/** Підпис кнопки створення запису (прототип v4: «+ Створити мандат», «+ New mandate»): назва типу в однині, українською — у знахідному відмінку. */
import type { Lang } from '@platform/core/types';
import { lowerFirst } from '../../menu/menuTypeName';

/** Істоти чоловічого роду: знахідний відмінок = родовий («помічника», «члена комітету»). */
const ANIMATE: Readonly<Record<string, string>> = {
  автор: 'автора', адресат: 'адресата', волонтер: 'волонтера', відвідувач: 'відвідувача', гість: 'гостя', громадянин: 'громадянина',
  делегат: 'делегата', депутат: 'депутата', донор: 'донора', експерт: 'експерта', кандидат: 'кандидата', клієнт: 'клієнта', ментор: 'ментора',
  одержувач: 'одержувача', опонент: 'опонента', помічник: 'помічника', постачальник: 'постачальника', представник: 'представника',
  прихильник: 'прихильника', промовець: 'промовця', підписант: 'підписанта', рецензент: 'рецензента', речник: 'речника', свідок: 'свідка',
  учасник: 'учасника', утримувач: 'утримувача', член: 'члена', власник: 'власника', координатор: 'координатора', виконавець: 'виконавця',
};

/** Середній рід на подвоєний приголосний + я («завдання», «скликання», «засідання») у знахідному не змінюється. */
const NEUTER = /([бвгґджзклмнпрстфхцчшщ])\1я$/;
/** Типові закінчення прикметника жіночого роду («бюджетна», «правова», «менторська», «зачеплена») — тоді змінюється й наступне слово. */
const ADJECTIVE = /(?:ськ|цьк|зьк|чн|тн|льн|жн|шн|рн|дн|вн|ов|ев|ен|ан|ин|ій|ш|ж|ч)а$/;
const PUNCT = /[,.;:]+$/;
const uk = (s: string) => s.toLocaleLowerCase('uk-UA');

/** Слово жіночого роду на -а/-я у знахідному (-у/-ю), «стаття» → «статтю»; інше слово — null. */
function feminine(word: string): string | null {
  const bare = word.replace(PUNCT, '');
  const tail = word.slice(bare.length);
  if (uk(bare) === 'стаття') return `${bare.slice(0, -2)}тю${tail}`;
  if (!/[а-яіїєґ'’][ая]$/i.test(bare) || NEUTER.test(uk(bare))) return null;
  return `${bare.slice(0, -1)}${bare.endsWith('а') ? 'у' : 'ю'}${tail}`;
}

/**
 * Знахідний відмінок назви в однині (чиста функція): перше слово — з малої літери (крім абревіатур); істота чоловічого роду — за словником;
 * слово жіночого роду на -а/-я → -у/-ю, а після прикметника жіночого роду — і наступне слово («бюджетну статтю», «політичну партію»);
 * решта («звіт», «завдання», «групу виборців») — без змін.
 */
export function accusativeUk(singular: string): string {
  const words = singular.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const first = lowerFirst(words[0] ?? '', 'uk');
  const animate = ANIMATE[uk(first)];
  if (animate) return [animate, ...words.slice(1)].join(' ');
  const head = feminine(first);
  if (head === null) return [first, ...words.slice(1)].join(' ');
  const next = words[1];
  const agreed = next !== undefined && ADJECTIVE.test(uk(first)) ? feminine(next) : null;
  return [head, ...(next !== undefined ? [agreed ?? next] : []), ...words.slice(2)].join(' ');
}

/**
 * Підпис «+ Створити <назва>» / «+ New <name>»: власний підпис типу `own` з пакета модуля (прототип v4 CREATE_UK: «акаунт», «члена партії»;
 * готовий підпис із «+» — як є), інакше назва однини у знахідному відмінку; без назви — загальний «+ Створити». `t` — переклад рядків рушія.
 */
export function createLabel(singular: string, lang: Lang, t: (key: string, vars?: Record<string, string>) => string, own?: string): string {
  const custom = own?.trim();
  if (custom) return custom.startsWith('+') ? custom : t('listbar.create', { name: custom });
  const name = singular.trim();
  if (!name) return t('listbar.createAny');
  const accusative = lang === 'uk' ? accusativeUk(name) : name.split(/\s+/).map((w) => lowerFirst(w, 'en')).join(' ');
  return t('listbar.create', { name: accusative });
}
